import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';

import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

import { useClient } from '../../context/ClientContext';
import { stripeConnectService } from '../../services/stripeConnectService';

const {
  FiAlertCircle,
  FiCheckCircle,
  FiCreditCard,
  FiExternalLink,
  FiLink,
  FiLoader,
  FiRefreshCw,
  FiShield,
  FiDollarSign
} = FiIcons;

const normalizeRole = (role) =>
  String(role || '')
    .toLowerCase()
    .trim();

const canManagePayments = (role) =>
  ['owner', 'owner_member', 'admin'].includes(
    normalizeRole(role)
  );

const getConnectStatusLabel = (account) => {
  if (!account) {
    return 'Not connected';
  }

  if (
    account.onboarding_complete ||
    (
      account.details_submitted &&
      account.charges_enabled &&
      account.payouts_enabled
    )
  ) {
    return 'Connected';
  }

  if (account.details_submitted) {
    return 'Verification pending';
  }

  return 'Setup required';
};

const getConnectStatusClasses = (account) => {
  const status = getConnectStatusLabel(account);

  if (status === 'Connected') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }

  if (status === 'Verification pending') {
    return 'border-amber-200 bg-amber-50 text-amber-700';
  }

  if (status === 'Setup required') {
    return 'border-blue-200 bg-blue-50 text-blue-700';
  }

  return 'border-slate-200 bg-slate-50 text-slate-600';
};

export default function Payments() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeClient } = useClient();

  const organizationId = activeClient?.id;
  const organizationName =
    activeClient?.name || 'Client organization';
  const canManage = canManagePayments(activeClient?.role);

  const [connectAccount, setConnectAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadConnectAccount = useCallback(
    async (showRefreshState = false) => {
      if (!organizationId) {
        setConnectAccount(null);
        setLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        if (showRefreshState) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError(null);

        const data = await stripeConnectService.getConnectAccount(
          organizationId
        );

        setConnectAccount(data?.account || null);
      } catch (loadError) {
        console.error(
          '[Payments] Stripe Connect load error:',
          loadError
        );

        setError(
          loadError?.message ||
            'Unable to load payment setup.'
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [organizationId]
  );

  useEffect(() => {
    setConnectAccount(null);
    setNotice(null);
    setError(null);

    loadConnectAccount();
  }, [organizationId, loadConnectAccount]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeConnect = params.get('stripe_connect');

    if (!stripeConnect) {
      return;
    }

    let cancelled = false;

    const handleReturn = async () => {
      if (stripeConnect === 'return') {
        if (!cancelled) {
          setNotice({
            type: 'success',
            text: 'Stripe setup was submitted. Checking the payment account status now.'
          });

          setError(null);
        }

        await loadConnectAccount(true);
      } else if (stripeConnect === 'refresh') {
        if (!cancelled) {
          setNotice({
            type: 'info',
            text: 'The Stripe setup session expired. Click Continue Stripe Setup to resume.'
          });
        }
      }

      params.delete('stripe_connect');

      const remainingSearch = params.toString();

      if (!cancelled) {
        navigate(
          {
            pathname: location.pathname,
            search: remainingSearch ? `?${remainingSearch}` : ''
          },
          {
            replace: true
          }
        );
      }
    };

    handleReturn();

    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    location.search,
    navigate,
    loadConnectAccount
  ]);

  const connectStatus = getConnectStatusLabel(connectAccount);
  const connectStatusClasses =
    getConnectStatusClasses(connectAccount);
  const isConnected = connectStatus === 'Connected';
  const hasStartedSetup = Boolean(
    connectAccount?.stripe_account_id
  );

  const handleStripeConnect = async () => {
    if (!organizationId || !canManage || connecting) {
      return;
    }

    try {
      setConnecting(true);
      setError(null);
      setNotice(null);

      const result =
        await stripeConnectService.createAccountLink(
          organizationId
        );

      const onboardingUrl =
        result?.onboardingUrl ||
        result?.url ||
        result?.accountLinkUrl ||
        result?.account_link_url;

      if (!onboardingUrl) {
        throw new Error(
          'Stripe did not return an onboarding URL.'
        );
      }

      let destination;

      try {
        destination = new URL(onboardingUrl);
      } catch {
        throw new Error(
          'Stripe returned an invalid onboarding URL.'
        );
      }

      if (
        destination.protocol !== 'https:' &&
        destination.protocol !== 'http:'
      ) {
        throw new Error(
          'The Stripe onboarding URL is invalid.'
        );
      }

      window.location.assign(destination.toString());
    } catch (connectError) {
      console.error(
        '[Payments] Stripe Connect setup error:',
        connectError
      );

      setError(
        connectError?.message ||
          'Unable to start Stripe payment setup.'
      );
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-180px)] items-center justify-center px-6">
        <div className="flex w-full max-w-sm flex-col items-center rounded-[32px] border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/10">
            <SafeIcon
              icon={FiCreditCard}
              className="text-xl"
            />
          </div>

          <div className="mb-4 flex items-center gap-2">
            <SafeIcon
              icon={FiLoader}
              className="animate-spin text-blue-600"
            />

            <span className="text-sm font-bold text-slate-700">
              Loading payments
            </span>
          </div>

          <p className="text-xs leading-5 text-slate-400">
            Checking the Stripe payment account for this organization.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-slate-50/40">
      <div className="mx-auto max-w-[1180px] px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
          <div className="relative px-6 py-7 md:px-8">
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-emerald-100/60 blur-3xl" />

            <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-center">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/10">
                  <SafeIcon
                    icon={FiCreditCard}
                    className="text-lg"
                  />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-black tracking-tight text-slate-900">
                      Payments
                    </h1>

                    {organizationId && (
                      <span
                        className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${connectStatusClasses}`}
                      >
                        {connectStatus}
                      </span>
                    )}
                  </div>

                  <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                    Set up how {organizationName} receives payments from customers.
                  </p>
                </div>
              </div>

              {organizationId && (
                <button
                  type="button"
                  onClick={() => loadConnectAccount(true)}
                  disabled={refreshing || connecting}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <SafeIcon
                    icon={FiRefreshCw}
                    className={
                      refreshing
                        ? 'animate-spin'
                        : 'transition-transform group-hover:rotate-45'
                    }
                  />

                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              )}
            </div>
          </div>
        </div>

        {!organizationId && (
          <div className="mb-8 rounded-[28px] border border-amber-200 bg-amber-50 p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <SafeIcon icon={FiAlertCircle} />
              </div>

              <div>
                <h2 className="font-black text-amber-900">
                  No organization selected
                </h2>

                <p className="mt-1 text-sm leading-6 text-amber-800">
                  Select a client organization before configuring customer payments.
                </p>
              </div>
            </div>
          </div>
        )}

        {notice && (
          <div
            className={`mb-6 overflow-hidden rounded-2xl border shadow-sm ${
              notice.type === 'success'
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-blue-200 bg-blue-50'
            }`}
          >
            <div className="flex items-start gap-3 p-4">
              <SafeIcon
                icon={
                  notice.type === 'success'
                    ? FiCheckCircle
                    : FiAlertCircle
                }
                className={`mt-0.5 shrink-0 ${
                  notice.type === 'success'
                    ? 'text-emerald-600'
                    : 'text-blue-600'
                }`}
              />

              <p
                className={`text-sm font-semibold leading-6 ${
                  notice.type === 'success'
                    ? 'text-emerald-800'
                    : 'text-blue-800'
                }`}
              >
                {notice.text}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 overflow-hidden rounded-2xl border border-rose-200 bg-rose-50 shadow-sm">
            <div className="flex items-start gap-3 p-4">
              <SafeIcon
                icon={FiAlertCircle}
                className="mt-0.5 shrink-0 text-rose-600"
              />

              <div className="min-w-0">
                <p className="text-sm font-black text-rose-900">
                  Unable to load payments
                </p>

                <p className="mt-1 break-words text-sm font-medium leading-6 text-rose-700">
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        {organizationId && (
          <>
            <section className="mb-8 overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-sm">
              <div className="p-6 md:p-8">
                <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
                  <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-50 text-violet-700">
                      <SafeIcon
                        icon={FiLink}
                        className="text-lg"
                      />
                    </div>

                    <div>
                      <div className="flex flex-wrap items-center gap-3">
                        <h2 className="text-lg font-black tracking-tight text-slate-900">
                          Stripe
                        </h2>

                        <span
                          className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wider ${connectStatusClasses}`}
                        >
                          {connectStatus}
                        </span>
                      </div>

                      <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                        Connect Stripe so {organizationName} can accept online payments from customers through invoices created in ProfitYO.
                      </p>

                      {connectAccount?.stripe_account_id && (
                        <p className="mt-2 text-xs font-semibold text-slate-400">
                          Stripe account:{' '}
                          {connectAccount.stripe_account_id}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0">
                    {!isConnected ? (
                      <button
                        type="button"
                        onClick={handleStripeConnect}
                        disabled={!canManage || connecting}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-xs font-bold text-white shadow-sm transition-all hover:bg-slate-800 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {connecting ? (
                          <>
                            <SafeIcon
                              icon={FiLoader}
                              className="animate-spin"
                            />

                            Opening Stripe...
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiLink} />

                            {hasStartedSetup
                              ? 'Continue Stripe Setup'
                              : 'Connect Stripe'}

                            <SafeIcon icon={FiExternalLink} />
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-xs font-bold text-emerald-700">
                        <SafeIcon icon={FiCheckCircle} />
                        Stripe connected
                      </div>
                    )}
                  </div>
                </div>

                {!canManage && (
                  <div className="mt-5 flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <SafeIcon
                      icon={FiShield}
                      className="mt-0.5 shrink-0 text-slate-500"
                    />

                    <div>
                      <p className="text-xs font-black text-slate-700">
                        Payment setup is restricted
                      </p>

                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        You can view this payment connection, but only authorized owners or administrators can configure Stripe.
                      </p>
                    </div>
                  </div>
                )}

                {isConnected && (
                  <div className="mt-6 grid gap-3 sm:grid-cols-3">
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Account
                      </p>

                      <p className="mt-1 text-sm font-bold text-slate-800">
                        Connected
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Payments
                      </p>

                      <p
                        className={`mt-1 text-sm font-bold ${
                          connectAccount?.charges_enabled
                            ? 'text-emerald-700'
                            : 'text-amber-700'
                        }`}
                      >
                        {connectAccount?.charges_enabled
                          ? 'Enabled'
                          : 'Pending'}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                        Payouts
                      </p>

                      <p
                        className={`mt-1 text-sm font-bold ${
                          connectAccount?.payouts_enabled
                            ? 'text-emerald-700'
                            : 'text-amber-700'
                        }`}
                      >
                        {connectAccount?.payouts_enabled
                          ? 'Enabled'
                          : 'Pending'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="overflow-hidden rounded-[32px] border border-slate-200 bg-slate-900 text-white shadow-xl shadow-slate-900/10">
              <div className="relative p-6 md:p-8">
                <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-emerald-500/10 blur-3xl" />

                <div className="relative">
                  <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5">
                      <SafeIcon
                        icon={FiDollarSign}
                        className="text-emerald-300"
                      />
                    </div>

                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                        Customer payments
                      </p>

                      <h2 className="mt-1 text-xl font-black tracking-tight">
                        How payments work
                      </h2>
                    </div>
                  </div>

                  <p className="max-w-3xl text-sm leading-6 text-slate-400">
                    When an invoice is created for {organizationName}, the invoice belongs to {organizationName}. Customers can pay through the secure Stripe payment page, and payments are processed through {organizationName}'s connected Stripe account.
                  </p>

                  <div className="mt-6 grid gap-3 md:grid-cols-3">
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                        01
                      </p>

                      <p className="mt-2 text-sm font-bold text-white">
                        Create invoice
                      </p>

                      <p className="mt-1 text-xs leading-5 text-slate-400">
                        Create an invoice while working inside {organizationName}.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                        02
                      </p>

                      <p className="mt-2 text-sm font-bold text-white">
                        Customer pays
                      </p>

                      <p className="mt-1 text-xs leading-5 text-slate-400">
                        The customer opens the secure invoice payment page and submits payment.
                      </p>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">
                        03
                      </p>

                      <p className="mt-2 text-sm font-bold text-white">
                        Business receives funds
                      </p>

                      <p className="mt-1 text-xs leading-5 text-slate-400">
                        Payment is processed through {organizationName}'s Stripe account and paid out according to its Stripe payout settings.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}